import { format, parseISO, isValid, formatDistanceToNow } from 'date-fns';

// Number formatters
export const formatNumber = (num) => {
  if (num === null || num === undefined) return 'N/A';
  const number = Number(num);
  if (isNaN(number)) return 'N/A';
  
  if (number >= 1000000) {
    return (number / 1000000).toFixed(1) + 'M';
  } else if (number >= 1000) {
    return (number / 1000).toFixed(1) + 'K';
  }
  return number.toLocaleString();
};

export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined) return 'N/A';
  const number = Number(value);
  if (isNaN(number)) return 'N/A';
  return `${number.toFixed(decimals)}%`;
};

export const formatRiskScore = (score) => {
  if (score === null || score === undefined) return 'N/A';
  const number = Number(score);
  if (isNaN(number)) return 'N/A';
  return number.toFixed(1);
};

// Date formatters
export const formatDate = (dateString, formatString = 'MMM dd, yyyy') => {
  if (!dateString) return 'Unknown';
  
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
    return isValid(date) ? format(date, formatString) : 'Invalid Date';
  } catch (error) {
    return 'Invalid Date';
  }
};

export const formatDateTime = (dateString) => {
  return formatDate(dateString, 'MMM dd, yyyy HH:mm');
};

export const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Unknown';
  
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
    return isValid(date) ? formatDistanceToNow(date, { addSuffix: true }) : 'Invalid Date';
  } catch (error) {
    return 'Invalid Date';
  }
};

export const formatISODate = (dateString) => {
  return formatDate(dateString, 'yyyy-MM-dd');
};

// Text formatters
export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatMutationName = (mutation) => {
  if (!mutation) return 'Unknown';
  return mutation.toUpperCase();
};

export const formatCountryName = (country) => {
  if (!country) return 'Unknown';
  return country.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

export const formatHostName = (host) => {
  if (!host) return 'Unknown';
  return host.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const formatCategory = (category) => {
  if (!category) return 'Other';
  return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Risk and Alert formatters
export const getRiskColor = (riskLevel) => {
  const colors = {
    'CRITICAL': '#dc2626',
    'HIGH': '#ea580c',
    'MEDIUM': '#d97706',
    'LOW': '#16a34a',
    'UNKNOWN': '#6b7280'
  };
  return colors[riskLevel?.toUpperCase()] || colors.UNKNOWN;
};

export const getRiskLabel = (riskLevel) => {
  const labels = {
    'CRITICAL': 'Critical',
    'HIGH': 'High',
    'MEDIUM': 'Medium',
    'LOW': 'Low',
    'UNKNOWN': 'Unknown'
  };
  return labels[riskLevel?.toUpperCase()] || labels.UNKNOWN;
};

export const getAlertIcon = (alertLevel) => {
  const icons = {
    'RED': '',
    'ORANGE': '',
    'YELLOW': '',
    'GREEN': ''
  };
  return icons[alertLevel?.toUpperCase()] || '';
};

export const getBadgeColor = (riskLevel) => {
  const colors = {
    'CRITICAL': 'bg-red-100 text-red-800',
    'HIGH': 'bg-orange-100 text-orange-800',
    'MEDIUM': 'bg-yellow-100 text-yellow-800',
    'LOW': 'bg-green-100 text-green-800',
    'UNKNOWN': 'bg-gray-100 text-gray-800'
  };
  return colors[riskLevel?.toUpperCase()] || colors.UNKNOWN;
};

// Data structure formatters
export const formatSequenceId = (id) => {
  if (!id) return 'Unknown';
  // Format sequence IDs for better readability
  return id.replace(/^EPI_ISL_/, '');
};

export const formatBatchId = (batchId) => {
  if (!batchId) return 'Unknown';
  return batchId.replace('batch_', '');
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Chart data formatters
export const formatChartData = (data, labelKey, valueKey) => {
  if (!Array.isArray(data)) return [];
  
  return data.map(item => ({
    name: item[labelKey] || 'Unknown',
    value: Number(item[valueKey]) || 0,
    ...item
  }));
};

export const formatPieChartData = (data) => {
  if (!Array.isArray(data)) return [];
  
  const total = data.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  
  return data.map((item, index) => ({
    name: item._id || item.name || 'Unknown',
    value: Number(item.count) || 0,
    percentage: total > 0 ? Math.round((Number(item.count) || 0) / total * 100) : 0,
    fill: item.color || `hsl(${index * 45}, 70%, 50%)`
  }));
};

export const formatTimeSeriesData = (data, dateKey, valueKey) => {
  if (!Array.isArray(data)) return [];
  
  return data.map(item => ({
    date: formatISODate(item[dateKey]),
    value: Number(item[valueKey]) || 0,
    formatted_date: formatDate(item[dateKey]),
    ...item
  })).sort((a, b) => new Date(a.date) - new Date(b.date));
};

// Table data formatters
export const formatTableRow = (row, columns) => {
  const formatted = {};
  
  columns.forEach(col => {
    const value = row[col.key];
    
    switch (col.type) {
      case 'number':
        formatted[col.key] = formatNumber(value);
        break;
      case 'percentage':
        formatted[col.key] = formatPercentage(value);
        break;
      case 'date':
        formatted[col.key] = formatDate(value);
        break;
      case 'datetime':
        formatted[col.key] = formatDateTime(value);
        break;
      case 'risk':
        formatted[col.key] = getRiskLabel(value);
        break;
      case 'mutation':
        formatted[col.key] = formatMutationName(value);
        break;
      case 'country':
        formatted[col.key] = formatCountryName(value);
        break;
      case 'host':
        formatted[col.key] = formatHostName(value);
        break;
      default:
        formatted[col.key] = value || 'N/A';
    }
  });
  
  return formatted;
};

// URL and query formatters
export const formatQueryParams = (params) => {
  const filtered = Object.entries(params)
    .filter(([key, value]) => value !== null && value !== undefined && value !== '')
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  
  return new URLSearchParams(filtered).toString();
};

export const parseQueryParams = (queryString) => {
  const params = new URLSearchParams(queryString);
  const result = {};
  
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  
  return result;
};

// Export filename formatters
export const formatExportFilename = (prefix, batchId, format, timestamp = new Date()) => {
  const dateStr = format(timestamp, 'yyyy-MM-dd_HHmm');
  const batch = batchId ? `_${formatBatchId(batchId)}` : '';
  return `${prefix}${batch}_${dateStr}.${format}`;
};